import { RootState } from "../store";
import { getAllMessages } from "./chat.slice";
import { TMessage } from "./types";

/**
 * Finds the preceding human message.
 *
 * The source of messages can be either:
 * - the Redux store (using a messageId), or
 * - a direct list of messages (using an index).
 *
 * Exactly one of `fromStore` or `fromList` must be provided.
 *
 * @param source - Either the Redux RootState or a list of messages
 * @param options - Configuration describing how to locate the starting point
 * @param options.fromStore - Use messages from the store and search before the given messageId
 * @param options.fromStore.messageId - The ID of the message to search before
 * @param options.fromList - Use a message array and search before the given index
 * @param options.fromList.index - The index to search before
 *
 * @returns The preceding human message RIGHT BEFORE message with role "ai", or null if none is found.
 */
export function findLastHumanMessageBefore(
  source: RootState | TMessage[],
  options: {
    fromStore?: { messageId: string };
    fromList?: { index: number };
  }
): TMessage | null {
  let messages: TMessage[];
  let startIndex: number;

  if (options.fromStore) {
    if (!('fromStore' in options)) return null;

    messages = getAllMessages(source as RootState);
    startIndex = messages.findIndex(
      (message) => message.messageId === options.fromStore!.messageId
    );

    if (startIndex === -1) return null;
  } else if (options.fromList) {
    messages = source as TMessage[];
    startIndex = options.fromList.index;

    if (startIndex <= 0 || startIndex > messages.length) return null;
  } else {
    throw new Error(
      'findLastHumanMessageBefore: either fromStore or fromList must be provided'
    );
  }

  for (let i = startIndex - 1; i >= 0; i--) {
    if (messages[i].role === 'human') {
      return messages[i];
    }
  }

  return null;
}